import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { sendWsMessageRange } from '@/Utils/socket-actions/MessageActions/messageActions';
import { getPrevMessages } from '@/Utils/socket-actions/getPrevMessages';
import { setScrollCallback } from '@/Utils/targetMessageService';
import { fetchStrangeUserData } from '@/api/member';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import {
    messageInRange,
    removeChannelMessages,
    resetRangeMessageMiddle,
} from '@/store/messages-reducer/messages-reducer';
import { messageDataSelector } from '@/store/messages-reducer/selectors-messages';
import { activeChannelIdSelector } from '@/store/server-data-reducer/selectors-server-data';
import { IMessageData } from '@/types';

const useMessageList = (props?: any) => {
    let secondCheckTriggered = false;

    const listRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef(null);
    const dispatch = useAppDispatch();

    const [isMessageListBottom, setIsMessageListBottom] = useState(true);
    const isPrependingRef = useRef(false);
    const isAppendingRef = useRef(false);
    const prevScrollMetricsRef = useRef({ height: 0, top: 0 });

    const rangeMessageSocketResponse = useAppSelector(
        state => state.messageReducer.rangeMessage,
    );

    const memberList = useAppSelector(
        state =>
            state.serverDataReducer.memberList[
                state.serverDataReducer.activeServer
            ],
    );
    const strangerMemberList = useAppSelector(
        state =>
            state.serverDataReducer.strangerMemberList[
                state.serverDataReducer.activeServer
            ],
    );

    const messageReplyHandler = useCallback((event: Object) => {
        props.onReplyMessage(event);
    }, []);

    const messages: IMessageData[] | any = useAppSelector(state =>
        messageDataSelector(state),
    );

    const memoizedMessages = useMemo(() => messages, [messages]);

    const [sortedDisplayBuffer, setSortedDisplayBuffer] = useState<
        IMessageData[]
    >([]);

    const [displayBuffer, setDisplayBuffer] = useState([]);
    const displayBufferRef = useRef<IMessageData[]>([]);
    const [loadMore, setLoadMore] = useState(false);
    const [targetScrollMessage, setTargetScrollMessage] = useState(null);

    const [messageRangeReady, setMessageRangeReady] = useState(false);
    const [isItemsMounted, setIsItemsMounted] = useState(false);

    const userId = useAppSelector(state => state.userReducer.user.id);
    const channelId = useAppSelector(activeChannelIdSelector);

    const scrollToEnd = (lastIndex?: any) => {
        if (viewRef.current && listRef.current) {
            viewRef?.current?.scrollToIndex({
                index: lastIndex ?? displayBuffer.length - 1,
            });
            setTimeout(() => {
                listRef.current.scrollTop = listRef.current?.scrollHeight;
            }, 150);
        }
    };

    const captureScrollMetrics = () => {
        if (listRef.current) {
            prevScrollMetricsRef.current = {
                height: listRef.current.scrollHeight,
                top: listRef.current.scrollTop,
            };
        }
    };

    // FOR STRANGER USERS
    const userIdSet = new Set();

    sortedDisplayBuffer.forEach(message => {
        userIdSet.add(message.user_id);
    });
    const uniqueUserIds: any[] = Array.from(userIdSet);

    uniqueUserIds?.map(item => {
        if (
            (memberList && memberList?.[item]) ||
            (strangerMemberList && strangerMemberList?.[item])
        ) {
            return;
        } else {
            fetchStrangeUserData({ user_id: item });
        }
    });
    // FOR STRANGER USERS

    const handleNewMessagesRender = () => {
        const lastIndex = memoizedMessages.length - 1;
        const lastMessage = memoizedMessages[lastIndex];
        if (
            displayBuffer.length + 1 === memoizedMessages.length &&
            lastMessage.user_id === userId &&
            isMessageListBottom
        ) {
            scrollToEnd(lastIndex);
        } else if (memoizedMessages.length > displayBuffer.length) {
            setLoadMore(false);
        }
        setDisplayBuffer(memoizedMessages);
        setTimeout(() => {
            setIsItemsMounted(true);
        }, 100);
    };

    const handleGetPrevMessages = (dateRange?: string) => {
        captureScrollMetrics();
        isPrependingRef.current = true;
        let dateStamp: any = messages[0]?.created_at;
        if (dateRange) {
            dateStamp = dateRange;
        }
        if (dateStamp && dateStamp !== '') {
            getPrevMessages(dateStamp, true);
        }
        setTimeout(() => {
            setLoadMore(false);
        }, 900);
    };

    const getNextMessages = (dateRange?: string) => {
        const currentBuffer = displayBufferRef.current;
        let dateStamp: string | undefined =
            currentBuffer[currentBuffer.length - 1]?.created_at;

        if (dateRange) {
            dateStamp = dateRange;
        }
        if (dateStamp) {
            getPrevMessages(dateStamp, false);
        }
    };

    const scrollListToBottom = () => {
        setTimeout(() => {
            if (
                listRef.current &&
                listRef.current.scrollTop !== listRef.current?.scrollHeight
            ) {
                listRef.current.scrollTop = listRef.current?.scrollHeight;
            }
        }, 300);
    };

    const shouldRemoveScrollListener = useRef(false);

    const scrollToMessageHandler = target => {
        setTargetScrollMessage(target);
        const indexOfTarget = displayBuffer.findIndex(
            el =>
                el?._id === target?._id ||
                el?._id === target?.original_message_id,
        );

        if (indexOfTarget !== -1) {
            if (viewRef?.current) {
                viewRef.current?.scrollToIndex({
                    index: indexOfTarget,
                });
            } else {
                setTimeout(() => {
                    if (viewRef?.current) {
                        viewRef.current?.scrollToIndex({
                            index: indexOfTarget,
                        });
                    }
                }, 400);
            }
        } else if (indexOfTarget === -1 && !messageRangeReady) {
            sendWsMessageRange(target?._id || target?.original_message_id);
        }
    };
    const handleScroll = event => {
        const target = event.currentTarget;
        const isBottom =
            target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
        setIsMessageListBottom(isBottom);
    };
    const getLastMessages = () => {
        dispatch(removeChannelMessages([channelId]));
        const dateStamp = new Date();
        setTargetScrollMessage(null);
        setMessageRangeReady(false);
        dispatch(resetRangeMessageMiddle());
        dispatch(messageInRange({ state: false, data: [] }));
        getPrevMessages(dateStamp.toISOString(), true);
    };

    useLayoutEffect(() => {
        return () => {
            if (rangeMessageSocketResponse.state) {
                setMessageRangeReady(false);
                const dateStamp = new Date();
                dispatch(messageInRange({ state: false, data: [] }));
                getPrevMessages(dateStamp.toISOString(), true);
            }
            setTargetScrollMessage(null);
            dispatch(resetRangeMessageMiddle());
        };
    }, [channelId]);

    useLayoutEffect(() => {
        if (
            listRef.current &&
            messages?.[messages.length - 1]?.thread_last_message
        ) {
            const container = listRef.current;
            const isAtBottom =
                container.scrollHeight - container.scrollTop ===
                container.clientHeight;

            if (isAtBottom) {
                scrollToEnd(messages.length - 1);
            }
        }
    }, [messages[messages.length - 1]?.thread_last_message, listRef.current]);

    useLayoutEffect(() => {
        listRef.current && (listRef.current.style.scrollBehavior = 'auto');
        if (
            listRef.current &&
            listRef.current.scrollTop !== listRef.current?.scrollHeight
        ) {
            listRef.current.scrollTop = listRef.current?.scrollHeight;
            if (!secondCheckTriggered) {
                secondCheckTriggered = true;
                scrollListToBottom();
            }
        }
    }, [channelId, listRef.current]);

    useLayoutEffect(() => {
        return () => {
            setTargetScrollMessage(null);
            setIsItemsMounted(false);
            dispatch(messageInRange({ state: false, data: [] }));
        };
    }, [channelId]);

    useLayoutEffect(() => {
        const ulElement = listRef.current;
        if (ulElement === undefined) return;
        const handleScrolling = () => {
            if (shouldRemoveScrollListener.current) {
                return;
            }

            if (Math.abs(ulElement.scrollTop) <= 5) {
                displayBuffer.length > 49 && setLoadMore(true);
                if (listRef.current) {
                    listRef.current.style.scrollBehavior = 'auto';
                }
                ulElement?.removeEventListener('scroll', handleScrolling);
                shouldRemoveScrollListener.current = true;
                handleGetPrevMessages();
            }

            if (
                displayBuffer?.[displayBuffer.length - 1]?.created_at &&
                rangeMessageSocketResponse.state
            ) {
                const isAtBottom =
                    Math.abs(Math.ceil(ulElement.scrollTop)) >=
                    Math.ceil(
                        ulElement.scrollHeight -
                            ulElement.getBoundingClientRect().height,
                    );

                if (isAtBottom) {
                    captureScrollMetrics();
                    isAppendingRef.current = true;
                    setIsMessageListBottom(false);
                    const lastCreatedAt =
                        displayBufferRef.current[
                            displayBufferRef.current.length - 1
                        ]?.created_at;
                    getNextMessages(lastCreatedAt);
                }
            }
        };

        shouldRemoveScrollListener.current = false;

        handleNewMessagesRender();
        ulElement?.addEventListener('scroll', handleScrolling);

        return () => {
            ulElement?.removeEventListener('scroll', handleScrolling);
        };
    }, [listRef.current, memoizedMessages]);

    useLayoutEffect(() => {
        if (!listRef.current) return;
        const container = listRef.current;
        container.style.scrollBehavior = 'auto';
        if (isPrependingRef.current) {
            const newScrollHeight = container.scrollHeight;
            const delta = newScrollHeight - prevScrollMetricsRef.current.height;
            container.scrollTop = prevScrollMetricsRef.current.top + delta;
            isPrependingRef.current = false;
        } else if (isAppendingRef.current) {
            container.scrollTop = prevScrollMetricsRef.current.top;
            isAppendingRef.current = false;
        }
    }, [displayBuffer]);

    useEffect(() => {
        displayBufferRef.current = displayBuffer as unknown as IMessageData[];
    }, [displayBuffer]);

    useLayoutEffect(() => {
        if (
            rangeMessageSocketResponse.state &&
            rangeMessageSocketResponse.data.length > 0 &&
            !messageRangeReady
        ) {
            const isInData = rangeMessageSocketResponse.data.find(
                msg => msg?._id === targetScrollMessage?._id,
            );
            if (isInData) {
                setMessageRangeReady(true);
            }
        }
    }, [rangeMessageSocketResponse]);

    useLayoutEffect(() => {
        setScrollCallback(scrollToMessageHandler);
        const sortedData = [...displayBuffer]?.sort(
            (a, b) =>
                new Date(a?.created_at)?.getTime() -
                new Date(b?.created_at)?.getTime(),
        );
        setSortedDisplayBuffer(sortedData);
    }, [displayBuffer]);

    useLayoutEffect(() => {
        if (messageRangeReady) {
            scrollToMessageHandler(targetScrollMessage);
        }
    }, [messageRangeReady]);

    return {
        displayBuffer,
        messageReplyHandler,
        listRef,
        loadMore,
        handleGetPrevMessages,
        viewRef,
        handleScroll,
        getLastMessages,
        targetScrollMessage,
        scrollToEnd,
        isItemsMounted,
        isMessageListBottom,
        setIsMessageListBottom,
        sortedDisplayBuffer,
    };
};

export default useMessageList;
